import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Plus } from "lucide-react";

export default function MyDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = deals.filter((d) => {
    if (filter !== "all" && d.status !== filter) return false;
    if (q && !d.property_address.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="mydeals-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Deal tracker</div>
          <h1 className="h-display text-4xl">My deals</h1>
        </div>
        <Link to="/submit" className="btn-primary flex items-center gap-2" data-testid="new-deal-button">
          <Plus size={16} /> New deal
        </Link>
      </div>

      <div className="surface-card p-4 mb-6 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <input
            placeholder="Search by address..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-base py-2 text-sm"
            data-testid="search-input"
          />
        </div>
        <select className="input-base py-2 text-sm w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="status-filter">
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewing">Reviewing</option>
          <option value="offered">Offered</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: "var(--text-muted)" }} className="text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <p className="text-lg mb-2">No deals match.</p>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Try a different filter or submit a new deal.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((d) => (
            <Link
              to={`/deals/${d.id}`}
              key={d.id}
              className="surface-card p-5 transition hover:-translate-y-0.5"
              style={{
                transition: "transform 0.18s, border-color 0.18s",
                borderColor: d.is_unread ? "var(--border-lime)" : undefined,
                background: d.is_unread ? "rgba(197,226,106,0.04)" : undefined,
              }}
              data-testid={`mydeals-card-${d.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-mono text-xs uppercase tracking-wider mb-1 flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                    {d.is_unread && (
                      <span
                        title="Coach replied"
                        data-testid={`student-unread-dot-${d.id}`}
                        style={{
                          width: 8, height: 8, borderRadius: 999,
                          background: "var(--lime)",
                          boxShadow: "0 0 8px rgba(197,226,106,0.7)",
                          flexShrink: 0,
                          animation: "wuPulse 1.6s ease-in-out infinite",
                        }}
                      />
                    )}
                    {d.submission_type === "jv" ? "JV Deal" : "Property Analysis"}
                    {d.is_unread && (
                      <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}>
                        Coach replied
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-lg leading-tight">{d.property_address}</div>
                </div>
                <StatusBadge status={d.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>ARV</div>
                  <div className="font-mono">{fmtMoney(d.arv)}</div>
                </div>
                <div>
                  <div className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>Asking</div>
                  <div className="font-mono">{fmtMoney(d.asking_price)}</div>
                </div>
                <div>
                  <div className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>Repairs</div>
                  <div className="font-mono">{fmtMoney(d.repair_estimate)}</div>
                </div>
              </div>
              <div className="mt-3 text-xs font-mono" style={{ color: "var(--text-dim)" }}>{fmtDate(d.created_at)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
